<template lang="pug">
.dashboard-alerts__wrapper
    .dashboard-header-wrapper
        h1 Alerts

    .dashboard-table-wrapper
        Spinner(v-if='alertsLoading', width='100', height='100')

        .dashboard-search-wrapper.dashboard-search-alert-wrapper(v-if='!alertsLoading')
            Search(
                :query='queryFilter',
                :queryType='queryTypeFilter',
                :types='SearchTypes',
                :sortByScore="false"
                allTypeSearch,
                @queryChange='handleQueryChange',
                @queryTypeChange='handleQueryTypeChange',
                @filterChange='handleFilterQueryChange'
                @reset="componentHandleQueryReset"
            )

            SelectFilter(
                v-model='statusFilter',
                property='status',
                label='Status',
                :options='[ { key: "active", label: "Active" }, { key: "inactive", label: "Inactive" }, { key: "silent", label: "Silent" }, ]',
                @filterChange='handleFilterFnChange("statusFilterFn", $event)'
            )

            SelectFilter(
                v-model='typeFilter',
                property='objectType',
                label='Object Type',
                :options='[ { key: "account", label: "Account" }, { key: "audience", label: "Audience" }, { key: "cs", label: "Custom Segment" }, ]',
                @filterChange='handleFilterFnChange("typeFilterFn", $event)'
            )

            SelectFilter(
                v-model='alertTypeFilter',
                property='triggerType',
                label='Alert Type',
                :options='[ { key: "threshold", label: "Threshold" }, { key: "trend", label: "Trend" }, ]',
                @filterChange='handleFilterFnChange("alertTypeFilterFn", $event)'
            )

            SelectFilter(
                v-model='triggeredFilter',
                property='isTriggered',
                label='Triggered',
                placeholder='Both',
                :options='[ { key: true, label: "Yes" }, { key: false, label: "No" }, ]',
                @filterChange='handleFilterFnChange("triggeredFilterFn", $event)'
            )

            button.btn.btn-simple.filter-item-wrapper.reset-filter(
                :disabled="!hasFiltersActive" @click="resetFilters()"
            )
                svg-icon.cursor(title="reset filters" name='close' size="small")
                span Clear filters

        ElTable.dashboard-table(
            v-if='!alertsLoading',
            :data='paginatedData',
            ref='tableData',
            :default-sort='{ order: sortOrder, prop: sortBy }',
            header-row-class-name='dashboard-table-header-row',
            header-cell-class-name='dashboard-table-header-cell',
            :row-class-name='setRowClassName',
            cell-class-name='dashboard-tb-cell',
            empty-text='We could not find any match for your search',
            @sort-change='componentHandleSortChange',
            :style='{ minHeight: getTableMaxHeight() + "px", width: "100%" }',
            :max-height='getTableMaxHeight()'
        )
            ElTableColumn(label='Id',prop='id', align='center', class-name='centered-icon-column', header-align='center' width='100', :sort-method='sortColumnBy("id")', sortable)

            ElTableColumn(width='60', align='center', class-name='centered-icon-column')
                template(slot-scope='scope')
                    svg-icon(v-if='scope.row.isTriggered', name='warning', size='large', :title='getTrigerTitle(scope.row)')

            ElTableColumn(
                label='Type',
                prop='objectType',
                :formatter='getTypeFormatter',
                width='150',
                class-name='ft-w-med',
                :sort-method='sortColumnBy("objectType")',
                sortable
            )

            ElTableColumn(label='Object Id', prop='objectId', width='100', :sort-method='sortColumnBy("objectId")', sortable)

            ElTableColumn(
                label='Label',
                prop='label',
                class-name='ft-w-med word-break-normal',
                :sort-method='sortColumnBy("label")',
                sortable
            )

            ElTableColumn(label='Created By', prop='createdBy', class-name='ft-w-med', :sort-method='sortColumnBy("createdBy")', sortable)
                .dsb-column-emails(slot-scope='scope')
                    div.created-by(:title="scope.row.createdBy")
                        p.is-truncate {{ scope.row.createdBy }}
                    .tag-group
                        ElTag(type='info', :title="email", size='mini', v-for='(email, index) in additionnalEmails[scope.row.id].visibleMails', :key='index') {{ email }}
                        ElPopover(v-if='additionnalEmails[scope.row.id].othersMails.length > 0',  , width='400', trigger='hover', placement='bottom-end')
                            .tag-group
                                ElTag(type='info',:title="email", size='mini', v-for='(email, index) in additionnalEmails[scope.row.id].othersMails', :key='index') {{ email }}
                            ElTag(slot='reference', type='info', effect='plain', size='mini') +{{ additionnalEmails[scope.row.id].othersMails.length }}

            ElTableColumn(label='Created At', width='130', prop='createdAt', :sort-method='sortColumnBy("createdAt")', sortable)
                template(slot-scope='scope') {{ formatDate(scope.row.createdAt) }}

            ElTableColumn(label='Triggered At', width='120', prop='triggeredAt', :sort-method='sortColumnBy("triggeredAt")', sortable)
                template(slot-scope='scope')
                    div.full-width
                        div.is-truncate {{ formatTriggeredAtDate(scope.row.triggeredAt) }}

            ElTableColumn(label='Status', prop='status', width='100', :sort-method='sortColumnBy("status")', sortable)
                template(slot-scope='scope')
                    ElTag(:type='getStatusTagType(scope.row.status)', size='small') {{ scope.row.status }}

            ElTableColumn(
                label='Alert Type',
                prop='triggerType',
                :formatter='upperFirstFormatter',
                class-name='ft-w-med',
                width='120',
                :sort-method='sortColumnBy("triggerType")',
                sortable
            )

            ElTableColumn(
                label='Value',
                class-name='ft-w-med word-break-normal'
            )
                template(slot-scope='scope') {{ getValueString(scope.row) }}

            ElTableColumn(width='100')
                template(slot-scope='scope')
                    .reveal-on-hover(v-if="hasEditAlertRights || hasReadAlertRights" :title='isScopeObjectDeleted(scope.row) ? "The alert cannot be edited" : "Edit Alert"')
                        el-button(@click.stop='editAlert(scope.row)', type='image', size='small', :disabled='isScopeObjectDeleted(scope.row)')
                            svg-icon(name='edit', size='small')

            ElTableColumn(width='100')
                template(slot-scope='scope')
                    el-button.reveal-on-hover(
                        @click.stop='displayDeletionModal(scope.row)'
                        type='image'
                        size='small'
                        :title='"Delete Alert"'
                        :disabled="!hasEditAlertRights"
                    )
                        svg-icon(name='trash', size='small')

        ElPagination(
            v-if='!alertsLoading',
            background,
            layout='prev, pager, next, sizes',
            :total='filteredData.length',
            :page-size.sync='pageSize',
            :current-page.sync='currentPage',
            :page-sizes='[5, 10, 25, 50, 100]'
        )


        //- Confirmation modal for deleting an alert
        .overlay-close(v-if='displayConfirmationModal', @click='closeModal()')
        .modal.modal__small.modal-delete(v-if='displayConfirmationModal')
            ConfirmationModal(modalClassName='modal-wrapper')
                template(slot='content')
                    div You are about to delete selected alert :
                        br
                        span.is-highlighted__danger {{ alertToDelete.label }}
                    div Do you want to proceed ?
                template(slot='footer')
                    .footer-content.l-flex-row--between
                        .btn.btn--medium.btn--round(@click='closeModal()') Close
                        .btn.btn--medium.btn--round.btn-delete(@click.once='handleDeleteAlert()') Delete
</template>

<script>
import { isEmpty, upperFirst, isNil, toNumber, keyBy } from 'lodash'
import { mapActions, mapState, mapMutations, mapGetters } from 'vuex'
import dayjs from 'dayjs'

import { alertConditionToString } from '@/store/dashboard/helpers'
import { formatDate, parseFilterParams, stringifyFilterParams, objRemoveEmpty } from '@/utils/helpers'

import tablePaginationMixin from '@/mixins/tablePaginationMixin'
import tableOrderMixin from '@/mixins/tableOrderMixin'

import AlertForm from '@/components/dashboard/AlertForm.vue'
import ConfirmationModal from '@/components/common/confirmationModal'
import Search from '@/components/common/Search'
import SelectFilter from '@/components/common/SelectFilter'
import Spinner from '@/components/common/Spinner'

export default {
    name: 'Alerts',

    mixins: [tablePaginationMixin, tableOrderMixin],

    components: {
        AlertForm,
        ConfirmationModal,
        Search,
        SelectFilter,
        Spinner
    },

    data() {
        return {
            alertToDelete: null,
            displayConfirmationModal: false,
            apiAlreadyCalled: false,

            // order state utils
            defaultSortBy: 'isTriggered',
            secondSortBy: 'createdAt',
            sortBy: 'isTriggered',

            // filter state utils
            SearchTypes: { objectId: 'object Id', label: 'label', id: 'id' },
            queryFilter: null,
            queryTypeFilter: null,
            queryFilterFn: data => data,
            statusFilter: null,
            statusFilterFn: () => true,
            typeFilter: null,
            typeFilterFn: () => true,
            alertTypeFilter: null,
            alertTypeFilterFn: () => true,
            triggeredFilter: null,
            triggeredFilterFn: () => true
        }
    },

    computed: {
        ...mapState('data/customTaxonomies', {
            segments: state => keyBy(Object.values(state.hierarchies).flat(), 'id')
        }),

        ...mapState('data/audience', {
            audiences: state => state.list
        }),

        ...mapState('dashboard', {
            alertsLoading: state => state.alertsLoading,
            alerts: state => state.alerts,
            displayAlertForm: state => state.displayAlertForm,
            selectedTimeRange: state => state.selectedTimeRange
        }),

        ...mapGetters('account', ['getCreateAlertsAllowed', 'getEditAlertsAllowed', 'getReadAlertsAllowed']),

        alertsArray() {
            return Object.values(this.alerts)
        },

        additionnalEmails() {
            return Object.entries(this.alerts).reduce((acc, [id, alert]) => {
                return { ...acc, [id]: this.getAdditionnalEmail(alert) }
            }, {})
        },

        filteredData() {
            let alerts = this.alertsArray
            if (this.queryFilter !== null && this.queryFilter?.trim() !== '') {
                alerts = alerts.filter(alert => {
                    let condition = false
                    if (this.queryTypeFilter === null || this.queryTypeFilter === 'label') {
                        condition = condition || alert?.label?.toLowerCase().includes(this.queryFilter?.toLowerCase())
                    }
                    if (this.queryTypeFilter === null) {
                        condition =
                            condition ||
                            (alert?.id && `${alert.id}`.includes(this.queryFilter)) ||
                            (alert?.objectId && `${alert.objectId}`.includes(this.queryFilter))
                    } else if (this.queryTypeFilter === 'id') {
                        condition = condition || (alert?.id && `${alert.id}`.startsWith(this.queryFilter))
                    } else if (this.queryTypeFilter === 'objectId') {
                        condition = condition || (alert?.objectId && `${alert.objectId}`.startsWith(this.queryFilter))
                    }
                    return condition
                })
            }
            return alerts
                .filter(this.typeFilterFn)
                .filter(this.alertTypeFilterFn)
                .filter(this.triggeredFilterFn)
                .filter(this.statusFilterFn)
        },

        sortedData() {
            const rows = [...this.filteredData]
            return rows.sort(this.sortColumnBy(this.sortBy))
        },

        paginatedData() {
            return this.paginate(this.sortedData)
        },

        filterUrlQuery() {
            const filters = {
                query: this.queryFilter,
                queryType: this.queryTypeFilter,
                status: this.statusFilter,
                type: this.typeFilter,
                alert: this.alertFilter,
                alertType: this.alertTypeFilter,
                triggered: this.triggeredFilter
            }

            return Object.keys(filters).reduce((obj, key) => {
                const filter = filters[key]
                if (filter !== undefined) {
                    obj[key] = stringifyFilterParams(filter)
                }
                return obj
            }, {})
        },

        hasFiltersActive() {
            const search = { ...this.filterUrlQuery }
            delete search.query
            delete search.queryType
            return Object.values(search).some(filter => filter !== null)
        },

        hasEditAlertRights() {
            return this.getEditAlertsAllowed()
        },

        hasReadAlertRights() {
            return this.getReadAlertsAllowed()
        }
    },

    watch: {
        filterUrlQuery: {
            handler(filters) {
                const urlFilters = this.$route.query
                if (Object.keys(filters).some(key => filters[key] != urlFilters[key])) {
                    this.$router.push({ query: objRemoveEmpty(filters) }).catch(err => err)
                }
            },
            deep: true
        },
        '$route.query': {
            handler(filters) {
                const {query, queryType, triggered} = filters
                if (query && queryType) {
                    this.queryFilter = query
                    this.queryTypeFilter = queryType
                }
                if (triggered) this.triggeredFilter = triggered
            },
            immediate: true,
            deep: true,
        }
    },

    beforeRouteEnter(to, _from, next) {
        next(vm => {
            vm.$nextTick(() => {
                vm.applyFilter(to.query)
            })
        })
    },

    async created() {
        if (isEmpty(this.alerts) || !this.apiAlreadyCalled) {
            await this.getAlerts()
            this.apiAlreadyCalled = true
        }
    },

    mounted() {
        const defaultEnDate = dayjs()
        const defaultstartDate = dayjs().subtract(7, 'days')
        const dateRange = [defaultstartDate.toISOString(), defaultEnDate.toISOString()]
        if (isNil(this.selectedTimeRange)) {
            this.setSelectedTimeRange({
                dateRange: dateRange
            })
        }
    },

    methods: {
        ...mapActions('dashboard', ['getAlerts', 'getTop', 'deleteAlert']),
        ...mapMutations('dashboard', ['setDisplayAlertForm', 'setSelectedTimeRange', 'setAlertInEdition']),

        getCreator(alert) {
            return alert?.notification?.configuration?.to?.[0]
        },

        getAdditionnalEmail(alert) {
            const configuration = alert?.notification?.configuration
            const mails = [configuration?.cc, configuration?.bcc].flat().filter(e => e)
            const visibleMails = mails.splice(0, 2)
            return { visibleMails, othersMails: mails }
        },

        getValueString(alert) {
            return alertConditionToString(alert)
        },

        getStatusTagType(type) {
            const _status = {
                active: 'success',
                silent: 'info',
                inactive: 'danger'
            }

            return _status[type]
        },

        formatDate(date) {
            return formatDate(date)
        },

        formatTriggeredAtDate(date) {
            return date ? formatDate(date, true) : ''
        },

        setRowClassName({ row }) {
            const triggeredAlertClass = row.isTriggered ? 'dashboard-tb-row-triggered prefixed' : ''
            return `dashboard-tb-row ${triggeredAlertClass}`
        },

        upperFirstFormatter(_row, _column, cellValue) {
            return upperFirst(cellValue)
        },

        getTypeFormatter(_row, _column, cellValue) {
            return cellValue === 'cs' || cellValue == 'segments' ? 'Custom Segment' : upperFirst(cellValue)
        },

        toggleAlertForm(value) {
            if (value) {
                this.getTop({ type: 'audiences' })
                this.getTop({ type: 'segments' })
            }
            this.setDisplayAlertForm({ value: value })
        },

        async handleFilterQueryChange({ fn, sortByScore }) {
            await this.$nextTick()
            const table = this.$refs.tableData
            if (sortByScore) this.componentHandleSortChange({ prop: 'score', order: 'ascending', table })
            this.queryFilterFn = fn
        },

        async componentHandleQueryReset() {
            await this.$nextTick()
            this.handleQueryReset(this.$refs.tableData)
        },

        displayDeletionModal(alert) {
            this.displayConfirmationModal = true
            this.alertToDelete = alert
        },

        closeModal() {
            this.displayConfirmationModal = false
            this.alertToDelete = null
        },

        handleDeleteAlert() {
            this.deleteAlert(toNumber(this.alertToDelete.id)).then(() => {
                this.closeModal()
            })
        },

        editAlert(alert) {
            this.setAlertInEdition({ alert })
            this.toggleAlertForm(true)
        },

        isScopeObjectDeleted(row) {
            if (row.objectType === 'cs') {
                const isCsDeleted = this.segments[toNumber(row.objectId)]
                return isCsDeleted === undefined
            } else if (row.objectType === 'audience') {
                const isAudienceDeleted = this.audiences.find(item => toNumber(item.id) === row.objectId)
                return isAudienceDeleted === undefined
            }
            return false
        },

        componentHandleSortChange(params) {
            this.currentPage = 1
            this.handleSortChange(params)
        },

        getTableMaxHeight() {
            return window.innerHeight - 350
        },

        async handleFilterFnChange(fn, value) {
            await this.$nextTick()
            this.currentPage = 1
            this[fn] = value
        },

        applyFilter(filters) {
            Object.entries(filters).forEach(([key, value]) => {
                const filter = key + 'Filter'
                this[filter] = parseFilterParams(value)
            })
        },

        handleQueryChange(value) {
            this.queryFilter = value
        },

        handleQueryTypeChange(value) {
            this.queryTypeFilter = value
        },

        getTrigerTitle(alert) {
            try {
                return JSON.parse(alert.lastEvent.information)['Information']['Description']
            } catch {
                return 'Alert is triggered, no further information'
            }
        },

        resetFilters() {
            this.triggeredFilter = null
            this.statusFilter = null
            this.alertTypeFilter = null
            this.typeFilter = null
        }
    }
}
</script>
